import { useCallback, useEffect, useState } from 'react';
import CreateIcon from '@mui/icons-material/Create';
import { useParams } from 'react-router-dom';
import DialogBox from '@/components/DialogBox';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import candidatesServices, {
  UpdateTopicName,
  NewNoteParams,
  UpdateNoteParams,
  DeleteTopicType,
  DeleteNoteParams,
  Candidate,
  PartialCandidateToSubmit,
  CandidateResponse,
} from '@/services/candidates';
import InputText from '@/trio-ui/InputText';
import Modal from '@/trio-ui/Modal';
import { CandidateTopicType } from '@/types/api';
import CandidateNotesCardComponent from './CandidateNotesCard.component';

export type CandidateNotesCardProps = CandidateTopicType & {
  onAddNewNote: () => void;
  onChangeCandidateField?: () => void;
  candidate: Candidate;
  autoScroll?: boolean;
  onAutoScroll?: () => void;
};

const CandidateNotesCard = ({
  onAddNewNote,
  onChangeCandidateField,
  notes: originalNotes,
  ...props
}: CandidateNotesCardProps): JSX.Element => {
  const [newTopicToAdd, setNewTopicToAdd] = useState({
    name: props.name,
    isOpen: false,
  });
  const [indexToDelete, setIndexToDelete] = useState<number | undefined>(undefined);
  const [topictoDelete, setTopicToDelete] = useState<string>('');

  const onSelectTopicToDelete = (id: string): void => {
    setTopicToDelete(id);
  };

  const toast = useToastContext();
  const { id } = useParams();
  const { profile } = useProfile();
  const [newTopicNote, setNewTopicNote] = useState('');
  const [candidate, setCandidate] = useState(props.candidate);
  const [notes, setNotes] = useState(
    originalNotes.map((note) => ({
      ...note,
      isEditing: false,
    }))
  );

  const skipEditingNote = useCallback(
    (prevNotes) => {
      return originalNotes.map((originalNote) => {
        const queryNoteById = (note): boolean => note.id === originalNote.id;
        const queriedNoteById = prevNotes.find(queryNoteById);

        if (queriedNoteById?.isEditing) {
          return queriedNoteById;
        }

        return {
          ...originalNote,
          isEditing: false,
        };
      });
    },
    [originalNotes]
  );

  useEffect(() => {
    setNotes(skipEditingNote);
  }, [skipEditingNote]);

  const addNewNoteMutation = useApiSubmit<any, NewNoteParams>(
    candidatesServices.addNewNoteToTheTopic
  );

  const updateNoteMutation = useApiSubmit<any, UpdateNoteParams>(
    candidatesServices.updateNoteToTheTopic
  );

  const updateTopicMutation = useApiSubmit<any, UpdateTopicName>(
    candidatesServices.updateTopicName
  );

  const deleteNoteMutation = useApiSubmit<any, DeleteNoteParams>(
    candidatesServices.deleteNoteToTheTopic
  );

  const deleteTopicMutation = useApiSubmit<any, DeleteTopicType>(candidatesServices.deleteTopic);

  const updateCandidateMutation = useApiSubmit<CandidateResponse, PartialCandidateToSubmit>(
    candidatesServices.updateCandidateIndividuallyById
  );

  const onChangeNewNote = (event): void => {
    const {
      target: { value },
    } = event;
    setNewTopicNote(value);
  };

  const onSaveNote = async (index?: number): Promise<void> => {
    try {
      const { id: topicId } = props;
      const editedNote = notes.find((note, noteIndex) => noteIndex === index);
      const methodToCall = editedNote ? updateNoteMutation : addNewNoteMutation;

      const payload = {
        id: id as string,
        topicId,
        text: editedNote ? editedNote?.text : newTopicNote,
        noteId: editedNote ? editedNote?.id : '',
      };

      await methodToCall.mutateAsync(payload);
      if (editedNote) {
        setNotes((prevNotes) => {
          return prevNotes.map((note) => ({
            ...note,
            isEditing: !note?.isEditing ? false : note?.id !== payload.noteId,
          }));
        });
      }
      setNewTopicNote('');
      onAddNewNote();
      const updateFeedback = 'The note has been updated.';
      const saveFeedback = 'The new note has been added.';
      toast.showSuccessFeedback(editedNote ? updateFeedback : saveFeedback);
    } catch (err) {
      const updateErrorFeedback = 'There was an error updating the note.';
      toast.showErrorFeedback(updateErrorFeedback);
    }
  };

  const onSaveSpecialFields = async (event: any): Promise<void> => {
    try {
      const { value, name } = event.target;

      await updateCandidateMutation.mutateAsync({
        candidate: {
          [name]: value,
          id,
        },
      });
      toast.showSuccessFeedback('The field has been updated.');
      onChangeCandidateField?.();
    } catch {
      toast.showErrorFeedback('There was an error updating the fields.');
    }
  };

  const onChangeSpecialFields = async (event: any): Promise<void> => {
    const { value, name } = event.target;

    setCandidate((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name !== 'salaryExpectation') {
      onSaveSpecialFields(event);
    }
  };

  const onEditNote = (index?: number): void => {
    setNotes(
      notes.map((note, key) => {
        if (key === index) {
          return {
            ...note,
            isEditing: true,
          };
        }
        return note;
      })
    );
  };

  const onCancelEditingNote = (index?: number): void => {
    if (index === undefined) {
      setNewTopicNote('');
      return;
    }

    setNotes(
      notes.map((note, key) => {
        if (key === index) {
          return {
            ...originalNotes[index],
            isEditing: false,
          };
        }
        return note;
      })
    );
  };

  const onUpdateNote = (event: any, index?: number): void => {
    const {
      target: { value },
    } = event;

    if (index === undefined) {
      setNewTopicNote(value);
      return;
    }

    setNotes(
      notes.map((note, key) => ({
        ...note,
        text: key === index ? value : note.text,
      }))
    );
  };

  const onDeleteNote = async (index?: number): Promise<void> => {
    try {
      const { id: topicId } = props;
      const noteToDelete = notes.find((note, noteIndex) => noteIndex === index);

      const payload = {
        id: id as string,
        topicId,
        noteId: noteToDelete ? noteToDelete?.id : '',
      };

      await deleteNoteMutation.mutateAsync(payload);
      setIndexToDelete(undefined);
      onAddNewNote();
      toast.showSuccessFeedback('The note has been deleted.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem deleting the note.');
    }
  };

  const onUpdateTopicName = async (): Promise<void> => {
    try {
      const { id: topicId } = props;

      const payload = {
        id: id as string,
        topicId,
        name: newTopicToAdd.name,
      };

      await updateTopicMutation.mutateAsync(payload);
      setNewTopicToAdd({
        isOpen: false,
        name: '',
      });
      onAddNewNote();
      toast.showSuccessFeedback('The topic has been renamed.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem renaming the topic.');
    }
  };

  const onDeleteTopic = async (): Promise<void> => {
    try {
      const { id: topicId } = props;

      const payload = {
        id: id as string,
        topicId,
      };

      await deleteTopicMutation.mutateAsync(payload);
      setTopicToDelete('');
      onAddNewNote();
      toast.showSuccessFeedback('The topic has been deleted.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem deleting the note.');
    }
  };

  const setIndexToDeleteHandler = (index?: number | undefined): void => {
    setIndexToDelete(index);
  };

  const onChangeTopicName = (event): void => {
    const {
      target: { value },
    } = event;

    setNewTopicToAdd((prev) => ({
      ...prev,
      name: value,
    }));
  };

  const onCloseModal = (): void => {
    setNewTopicToAdd({
      name: props.name,
      isOpen: false,
    });
  };

  const onOpenEditModal = (): void => {
    setNewTopicToAdd({
      name: props.name,
      isOpen: true,
    });
  };

  const isSubmiting =
    addNewNoteMutation.isLoading || updateNoteMutation.isLoading || deleteNoteMutation.isLoading;

  return (
    <>
      <CandidateNotesCardComponent
        onSaveSpecialFields={onSaveSpecialFields}
        onSelectTopicToDelete={onSelectTopicToDelete}
        isSubmiting={isSubmiting}
        onChangeSpecialFields={onChangeSpecialFields}
        userName={profile?.name ?? ''}
        onSaveNote={onSaveNote}
        onChangeHandler={onChangeNewNote}
        onEditNote={onEditNote}
        newTopicNote={newTopicNote}
        notes={notes}
        onUpdateNote={onUpdateNote}
        onDeleteNote={setIndexToDeleteHandler}
        onCancelEditingNote={onCancelEditingNote}
        onOpenEditModal={onOpenEditModal}
        {...props}
        candidate={candidate}
      />
      <Modal
        modalId="new-topic-name"
        isCentered
        size="sm"
        headerIcon={CreateIcon}
        onConfirmClick={onUpdateTopicName}
        modalTitle="Type a new name"
        onConfirmLabel="Save"
        isButtonDisabled={!newTopicToAdd.name}
        bodyRender={
          <InputText
            name="newtopic"
            placeholder=""
            value={newTopicToAdd.name}
            onChangeHandler={onChangeTopicName}
          />
        }
        onClose={onCloseModal}
        isOpen={newTopicToAdd.isOpen}
      />
      <DialogBox
        bodyText="Any notes inside this topic will be also deleted. This action cannot be undone."
        data-testid="delete-topic-dialog"
        isOpen={!!topictoDelete}
        onCancelHandler={() => setTopicToDelete('')}
        onConfirmHandler={() => onDeleteTopic()}
      />
      <DialogBox
        data-testid="delete-note-dialog"
        isOpen={indexToDelete != null}
        onCancelHandler={() => setIndexToDeleteHandler(undefined)}
        onConfirmHandler={() => onDeleteNote(indexToDelete)}
      />
    </>
  );
};

export default CandidateNotesCard;
